import React from 'react';
import { ToggleSwitch } from '../../ToggleSwitch/ToggleSwitch';
import { SettingCard, SettingCardProps } from '../SettingCard/SettingCard';
import './ToggleSetting.css';

// Toggle setting card props (includes all functionality)
export interface ToggleSettingCardProps extends Omit<
	SettingCardProps,
	'content' | 'className' | 'showContentSeparator'
> {
	checked: boolean;
	onChange: (checked: boolean) => void;

	// Status and info props
	ariaLabel?: string;

	// Warning and error handling
	warningCondition?: (checked: boolean) => boolean;
	warningMessage?: string;

	// Custom content
	children?: React.ReactNode;
	showContentSeparator?: boolean;

	// Additional styling
	className?: string;
}

export const ToggleSettingCard: React.FC<ToggleSettingCardProps> = ({
	title,
	description,
	checked,
	onChange,
	settingId,
	ariaLabel,
	warningCondition: _warningCondition, // Keep for backward compatibility, but not used (warnings are now static)
	warningMessage,
	disabled = false,
	children,
	showContentSeparator = false,
	className = '',
	learnMoreUrl,
	learnMoreText,
}) => {
	const showWarning = !!(_warningCondition && warningMessage);

	// Determine status type and message
	const getStatusInfo = () => {
		if (showWarning && warningMessage) {
			return { type: 'warning' as const, show: true, message: warningMessage };
		}
		return {
			type: 'info' as const,
			show: false,
			message: undefined as string | undefined,
		};
	};

	const statusInfo = getStatusInfo();

	const handleToggleChange = async (value: boolean) => {
		if (disabled) {
			return;
		}
		onChange(value);
	};

	return (
		<SettingCard
			description={description}
			settingId={settingId}
			showStatusOnValueChange={statusInfo.show}
			statusMessage={statusInfo.message}
			disabled={disabled}
			className={className}
			learnMoreUrl={learnMoreUrl}
			learnMoreText={learnMoreText}
			title={
				<>
					{typeof title === 'string' ? (
						<h3 className="setting-card-title">{title}</h3>
					) : (
						title
					)}
					<ToggleSwitch
						checked={checked}
						onChange={handleToggleChange}
						disabled={disabled}
						ariaLabel={ariaLabel || (typeof title === 'string' ? title : 'Toggle switch')}
						settingId={settingId}
					/>
				</>
			}
			content={
				children && (
					<div
						className={`toggle-custom-content ${
							showContentSeparator ? 'with-separator' : 'without-separator'
						}`}
					>
						{children}
					</div>
				)
			}
		/>
	);
};
